import type { Platform } from "./shortcut";
type ShortcutAction = { id: string; shortcut?: string; allowInEditor?: boolean; priority?: number };

/**
 * Shortcut strings, parsing, and matching. Pure: no DOM, no React. `useGlobalShortcuts` in
 * `useGlobalShortcuts.ts` wires this to a `window` keydown listener.
 *
 * Grammar: chords are joined with `+` (`mod+shift+]`), sequences are chords separated by
 * spaces (`g t`). Modifiers: `mod` (⌘ on Mac, Ctrl elsewhere), `ctrl`, `meta`/`cmd`, `alt`/
 * `option`, `shift`. Keys are `KeyboardEvent.key` values, case-insensitive, with a few
 * aliases (`esc`, `return`, `space`, `up`, `plus`).
 */

export type Chord = {
  /** Lower-case `KeyboardEvent.key`; shifted US symbols are stored unshifted (`?` → `/` + shift). */
  key: string;
  mod: boolean;
  ctrl: boolean;
  meta: boolean;
  alt: boolean;
  shift: boolean;
};

/** One or more chords pressed in order. */
export type ParsedShortcut = readonly Chord[];

/** The subset of `KeyboardEvent` matching needs, so tests can use plain objects. */
export type KeyEventLike = {
  key: string;
  code?: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
};

const KEY_ALIASES: Record<string, string> = {
  esc: "escape",
  return: "enter",
  space: " ",
  spacebar: " ",
  up: "arrowup",
  down: "arrowdown",
  left: "arrowleft",
  right: "arrowright",
  del: "delete",
  plus: "+",
  minus: "-",
};

/** US keyboard: what `event.key` reports when shift is held on a symbol key. */
const SHIFTED_SYMBOLS: Record<string, string> = {
  "~": "`",
  "!": "1",
  "@": "2",
  "#": "3",
  $: "4",
  "%": "5",
  "^": "6",
  "&": "7",
  "*": "8",
  "(": "9",
  ")": "0",
  _: "-",
  "+": "=",
  "{": "[",
  "}": "]",
  "|": "\\",
  ":": ";",
  '"': "'",
  "<": ",",
  ">": ".",
  "?": "/",
};

/** `KeyboardEvent.code` → key, for layouts/modifiers where `event.key` is not the plain key (⌥K on Mac gives `˚`). */
const CODE_KEYS: Record<string, string> = {
  BracketLeft: "[",
  BracketRight: "]",
  Comma: ",",
  Period: ".",
  Slash: "/",
  Backslash: "\\",
  Semicolon: ";",
  Quote: "'",
  Backquote: "`",
  Minus: "-",
  Equal: "=",
  Space: " ",
  Enter: "enter",
  Escape: "escape",
  Tab: "tab",
  Backspace: "backspace",
  Delete: "delete",
};

function keyFromCode(code: string | undefined): string | undefined {
  if (code === undefined) return undefined;
  if (code.startsWith("Key") && code.length === 4) return code.slice(3).toLowerCase();
  if (code.startsWith("Digit") && code.length === 6) return code.slice(5);
  return CODE_KEYS[code];
}

export function parseChord(text: string): Chord {
  const chord: Chord = { key: "", mod: false, ctrl: false, meta: false, alt: false, shift: false };
  // "mod++" (or a bare "+") means the key itself is "+"; "mod+" is a missing key.
  const endsWithPlus = text === "+" || text.endsWith("++");
  const parts = text
    .split("+")
    .map((p) => p.trim().toLowerCase())
    .filter((p) => p.length > 0);
  if (endsWithPlus) parts.push("+");
  for (const part of parts) {
    switch (part) {
      case "mod":
        chord.mod = true;
        break;
      case "ctrl":
      case "control":
        chord.ctrl = true;
        break;
      case "meta":
      case "cmd":
      case "command":
      case "super":
      case "win":
        chord.meta = true;
        break;
      case "alt":
      case "option":
      case "opt":
        chord.alt = true;
        break;
      case "shift":
        chord.shift = true;
        break;
      default:
        chord.key = KEY_ALIASES[part] ?? part;
    }
  }
  if (chord.key === "") throw new Error(`Shortcut chord "${text}" has no key`);
  const unshifted = SHIFTED_SYMBOLS[chord.key];
  if (unshifted !== undefined) {
    chord.key = unshifted;
    chord.shift = true;
  }
  return chord;
}

const parseCache = new Map<string, ParsedShortcut>();

/** Parses `"mod+k"`, `"mod+shift+]"`, `"c"`, or a sequence `"g t"`. Results are cached. */
export function parseShortcut(shortcut: string): ParsedShortcut {
  const cached = parseCache.get(shortcut);
  if (cached !== undefined) return cached;
  const chords = shortcut
    .trim()
    .split(/\s+/)
    .filter((c) => c.length > 0)
    .map(parseChord);
  if (chords.length === 0) throw new Error("Shortcut is empty");
  parseCache.set(shortcut, chords);
  return chords;
}

/** True when the chord uses ⌘/Ctrl/Meta, i.e. cannot be mistaken for typing. */
export function chordHasPrimaryModifier(chord: Chord): boolean {
  return chord.mod || chord.ctrl || chord.meta;
}

/** Every chord in the shortcut carries a primary modifier. */
export function shortcutHasPrimaryModifier(shortcut: ParsedShortcut): boolean {
  return shortcut.every(chordHasPrimaryModifier);
}

/** Whether a keyboard event is exactly this chord on the given platform. */
export function chordMatches(event: KeyEventLike, chord: Chord, platform: Platform): boolean {
  const wantMeta = chord.meta || (chord.mod && platform === "mac");
  const wantCtrl = chord.ctrl || (chord.mod && platform !== "mac");
  if (event.metaKey !== wantMeta) return false;
  if (event.ctrlKey !== wantCtrl) return false;
  if (event.altKey !== chord.alt) return false;
  if (event.shiftKey !== chord.shift) return false;
  const key = event.key.toLowerCase();
  if (key === chord.key) return true;
  if (chord.shift && SHIFTED_SYMBOLS[event.key] === chord.key) return true;
  return keyFromCode(event.code) === chord.key;
}

/** Whether a keyboard event matches a single-chord shortcut string, e.g. `eventMatches(e, "mod+k", "mac")`. */
export function eventMatches(event: KeyEventLike, shortcut: string, platform: Platform): boolean {
  const parsed = parseShortcut(shortcut);
  const first = parsed[0];
  return parsed.length === 1 && first !== undefined && chordMatches(event, first, platform);
}

/** Non-text inputs: keys pressed on them are not typing. */
const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

const EDITABLE_SELECTOR =
  'input, textarea, select, [contenteditable]:not([contenteditable="false"]), .cm-editor';

/**
 * True when a key event on `el` is typing (or editor-owned) and plain-key shortcuts must not
 * fire: inside `input`, `textarea`, `select`, a contenteditable, or a CodeMirror editor.
 */
export function shouldIgnoreTarget(el: unknown): boolean {
  if (typeof el !== "object" || el === null) return false;
  const element = el as { closest?: (selector: string) => Element | null };
  if (typeof element.closest !== "function") return false;
  const editable = element.closest(EDITABLE_SELECTOR);
  if (editable === null) return false;
  if (editable.tagName === "INPUT") {
    const type = (editable.getAttribute("type") ?? "text").toLowerCase();
    return !NON_TEXT_INPUT_TYPES.has(type);
  }
  return true;
}

export type ShortcutBinding = {
  id: string;
  shortcut: ParsedShortcut;
  priority: number;
  /** Registration order; later wins ties. */
  order: number;
};

/**
 * The shortcut bindings that may fire right now. Inside an editable target only
 * modifier-only shortcuts remain, and only for actions that have not set `allowInEditor: false`.
 */
export function bindingsFor<T extends ShortcutAction>(
  actions: readonly T[],
  inEditable: boolean,
): ShortcutBinding[] {
  const bindings: ShortcutBinding[] = [];
  actions.forEach((action, order) => {
    if (action.shortcut === undefined) return;
    const shortcut = parseShortcut(action.shortcut);
    if (inEditable && (action.allowInEditor === false || !shortcutHasPrimaryModifier(shortcut))) {
      return;
    }
    bindings.push({ id: action.id, shortcut, priority: action.priority ?? 0, order });
  });
  return bindings;
}

/** Chords typed so far towards a multi-chord sequence. Kept in a ref by the hook, never in React state. */
export type PendingSequence = { events: readonly KeyEventLike[]; since: number };

export type ResolveResult = {
  /** The action to run, if a binding matched completely. */
  actionId?: string;
  /** The new pending state (a sequence prefix in progress) or null. */
  pending: PendingSequence | null;
};

export const SEQUENCE_TIMEOUT_MS = 1000;

function snapshotEvent(event: KeyEventLike): KeyEventLike {
  const snap: KeyEventLike = {
    key: event.key,
    metaKey: event.metaKey,
    ctrlKey: event.ctrlKey,
    altKey: event.altKey,
    shiftKey: event.shiftKey,
  };
  if (event.code !== undefined) snap.code = event.code;
  return snap;
}

function resolveSequence(
  events: readonly KeyEventLike[],
  bindings: readonly ShortcutBinding[],
  platform: Platform,
): { full: ShortcutBinding | undefined; prefix: boolean } {
  let full: ShortcutBinding | undefined;
  let prefix = false;
  for (const binding of bindings) {
    if (events.length > binding.shortcut.length) continue;
    const matches = events.every((event, i) => {
      const chord = binding.shortcut[i];
      return chord !== undefined && chordMatches(event, chord, platform);
    });
    if (!matches) continue;
    if (events.length === binding.shortcut.length) {
      if (
        full === undefined ||
        binding.priority > full.priority ||
        (binding.priority === full.priority && binding.order > full.order)
      ) {
        full = binding;
      }
    } else {
      prefix = true;
    }
  }
  return { full, prefix };
}

/**
 * Decides what a keydown does given the eligible bindings and any sequence in progress.
 * A complete match wins over a longer sequence that shares the prefix (`g` beats `g t`).
 * When the pending prefix plus this key matches nothing, the key is retried on its own.
 */
export function resolveKey(
  event: KeyEventLike,
  bindings: readonly ShortcutBinding[],
  pending: PendingSequence | null,
  now: number,
  platform: Platform,
  timeoutMs: number = SEQUENCE_TIMEOUT_MS,
): ResolveResult {
  const current = snapshotEvent(event);
  const live = pending !== null && now - pending.since <= timeoutMs ? pending : null;
  const attempts: (readonly KeyEventLike[])[] =
    live === null ? [[current]] : [[...live.events, current], [current]];
  for (const events of attempts) {
    const { full, prefix } = resolveSequence(events, bindings, platform);
    if (full !== undefined) return { actionId: full.id, pending: null };
    if (prefix) return { pending: { events, since: now } };
  }
  return { pending: null };
}

/** Splits a shortcut string into its chords for display: `"g t"` → `["g", "t"]`. */
export function shortcutChords(shortcut: string): string[] {
  return shortcut
    .trim()
    .split(/\s+/)
    .filter((c) => c.length > 0);
}
